/**
 * Available Shopify API versions
 * @see https://shopify.dev/docs/api/usage/versioning
 *
 * Note: Shopify releases new API versions quarterly (January, April, July, October)
 * Each stable version is supported for a minimum of 12 months.
 * Versions older than 2023-10 may no longer be supported.
 */
export const ApiVersion = {
  // Stable versions (as of 2025)
  October25: '2025-10',
  July25: '2025-07',
  April25: '2025-04',
  January25: '2025-01',
  October24: '2024-10',
  July24: '2024-07',
  April24: '2024-04',
  January24: '2024-01',
  October23: '2023-10',

  // Special versions
  Unstable: 'unstable',
} as const;

export type ApiVersionValue = (typeof ApiVersion)[keyof typeof ApiVersion];

/**
 * Latest stable API version
 */
export const LATEST_API_VERSION = ApiVersion.October25;

/**
 * API version aliases for convenience
 */
export const API_VERSION_ALIASES = {
  latest: LATEST_API_VERSION,
  ...ApiVersion,
} as const;

export type ApiVersionAlias =
  (typeof API_VERSION_ALIASES)[keyof typeof API_VERSION_ALIASES];

// For backward compatibility, also export the old name
export const API_VERSIONS = ApiVersion;

/**
 * Webhook topic constants mapping GraphQL enum values to event names
 * @see https://shopify.dev/docs/api/admin-graphql/unstable/enums/WebhookSubscriptionTopic
 */
export const WebhookTopic = {
  // App
  APP_UNINSTALLED: 'app/uninstalled',
  APP_SUBSCRIPTIONS_UPDATE: 'app_subscriptions/update',
  APP_SUBSCRIPTIONS_APPROACHING_CAPPED_AMOUNT:
    'app_subscriptions/approaching_capped_amount',

  // Carts
  CARTS_CREATE: 'carts/create',
  CARTS_UPDATE: 'carts/update',

  // Checkouts
  CHECKOUTS_CREATE: 'checkouts/create',
  CHECKOUTS_UPDATE: 'checkouts/update',
  CHECKOUTS_DELETE: 'checkouts/delete',

  // Collections
  COLLECTIONS_CREATE: 'collections/create',
  COLLECTIONS_UPDATE: 'collections/update',
  COLLECTIONS_DELETE: 'collections/delete',

  // Customers
  CUSTOMERS_CREATE: 'customers/create',
  CUSTOMERS_UPDATE: 'customers/update',
  CUSTOMERS_DELETE: 'customers/delete',
  CUSTOMERS_DISABLE: 'customers/disable',
  CUSTOMERS_ENABLE: 'customers/enable',
  CUSTOMERS_DATA_REQUEST: 'customers/data_request',
  CUSTOMERS_REDACT: 'customers/redact',
  CUSTOMERS_EMAIL_MARKETING_CONSENT_UPDATE:
    'customers/email_marketing_consent/update',
  CUSTOMER_GROUPS_CREATE: 'customer_groups/create',
  CUSTOMER_GROUPS_UPDATE: 'customer_groups/update',
  CUSTOMER_GROUPS_DELETE: 'customer_groups/delete',
  CUSTOMER_PAYMENT_METHODS_CREATE: 'customer_payment_methods/create',
  CUSTOMER_PAYMENT_METHODS_UPDATE: 'customer_payment_methods/update',
  CUSTOMER_PAYMENT_METHODS_REVOKE: 'customer_payment_methods/revoke',

  // Disputes
  DISPUTES_CREATE: 'disputes/create',
  DISPUTES_UPDATE: 'disputes/update',

  // Draft Orders
  DRAFT_ORDERS_CREATE: 'draft_orders/create',
  DRAFT_ORDERS_UPDATE: 'draft_orders/update',
  DRAFT_ORDERS_DELETE: 'draft_orders/delete',

  // Fulfillments
  FULFILLMENTS_CREATE: 'fulfillments/create',
  FULFILLMENTS_UPDATE: 'fulfillments/update',
  FULFILLMENT_EVENTS_CREATE: 'fulfillment_events/create',
  FULFILLMENT_EVENTS_DELETE: 'fulfillment_events/delete',
  FULFILLMENT_ORDERS_CANCELLATION_REQUEST_ACCEPTED:
    'fulfillment_orders/cancellation_request_accepted',
  FULFILLMENT_ORDERS_CANCELLATION_REQUEST_REJECTED:
    'fulfillment_orders/cancellation_request_rejected',
  FULFILLMENT_ORDERS_CANCELLATION_REQUEST_SUBMITTED:
    'fulfillment_orders/cancellation_request_submitted',
  FULFILLMENT_ORDERS_CANCELLED: 'fulfillment_orders/cancelled',
  FULFILLMENT_ORDERS_FULFILLMENT_REQUEST_ACCEPTED:
    'fulfillment_orders/fulfillment_request_accepted',
  FULFILLMENT_ORDERS_FULFILLMENT_REQUEST_REJECTED:
    'fulfillment_orders/fulfillment_request_rejected',
  FULFILLMENT_ORDERS_FULFILLMENT_REQUEST_SUBMITTED:
    'fulfillment_orders/fulfillment_request_submitted',
  FULFILLMENT_ORDERS_FULFILLMENT_SERVICE_FAILED_TO_COMPLETE:
    'fulfillment_orders/fulfillment_service_failed_to_complete',
  FULFILLMENT_ORDERS_IN_PROGRESS: 'fulfillment_orders/in_progress',
  FULFILLMENT_ORDERS_LINE_ITEMS_PREPARED_FOR_LOCAL_DELIVERY:
    'fulfillment_orders/line_items_prepared_for_local_delivery',
  FULFILLMENT_ORDERS_LINE_ITEMS_PREPARED_FOR_PICKUP:
    'fulfillment_orders/line_items_prepared_for_pickup',
  FULFILLMENT_ORDERS_MOVED: 'fulfillment_orders/moved',
  FULFILLMENT_ORDERS_OPEN: 'fulfillment_orders/open',
  FULFILLMENT_ORDERS_ORDER_ROUTING_COMPLETE:
    'fulfillment_orders/order_routing_complete',
  FULFILLMENT_ORDERS_PLACED_ON_HOLD: 'fulfillment_orders/placed_on_hold',
  FULFILLMENT_ORDERS_READY_FOR_PICKUP: 'fulfillment_orders/ready_for_pickup',
  FULFILLMENT_ORDERS_RELEASED_FROM_HOLD:
    'fulfillment_orders/released_from_hold',
  FULFILLMENT_ORDERS_RESCHEDULED: 'fulfillment_orders/rescheduled',
  FULFILLMENT_ORDERS_SCHEDULED_FULFILLMENT_ORDER_READY:
    'fulfillment_orders/scheduled_fulfillment_order_ready',

  // Inventory
  INVENTORY_ITEMS_CREATE: 'inventory_items/create',
  INVENTORY_ITEMS_UPDATE: 'inventory_items/update',
  INVENTORY_ITEMS_DELETE: 'inventory_items/delete',
  INVENTORY_LEVELS_CONNECT: 'inventory_levels/connect',
  INVENTORY_LEVELS_UPDATE: 'inventory_levels/update',
  INVENTORY_LEVELS_DISCONNECT: 'inventory_levels/disconnect',

  // Locations
  LOCATIONS_CREATE: 'locations/create',
  LOCATIONS_UPDATE: 'locations/update',
  LOCATIONS_DELETE: 'locations/delete',
  LOCATIONS_ACTIVATE: 'locations/activate',
  LOCATIONS_DEACTIVATE: 'locations/deactivate',

  // Orders
  ORDERS_CREATE: 'orders/create',
  ORDERS_UPDATE: 'orders/update',
  ORDERS_DELETE: 'orders/delete',
  ORDERS_CANCELLED: 'orders/cancelled',
  ORDERS_EDITED: 'orders/edited',
  ORDERS_FULFILLED: 'orders/fulfilled',
  ORDERS_PAID: 'orders/paid',
  ORDERS_PARTIALLY_FULFILLED: 'orders/partially_fulfilled',
  ORDER_TRANSACTIONS_CREATE: 'order_transactions/create',

  // Products
  PRODUCTS_CREATE: 'products/create',
  PRODUCTS_UPDATE: 'products/update',
  PRODUCTS_DELETE: 'products/delete',
  PRODUCT_LISTINGS_ADD: 'product_listings/add',
  PRODUCT_LISTINGS_UPDATE: 'product_listings/update',
  PRODUCT_LISTINGS_REMOVE: 'product_listings/remove',
  PRODUCT_FEEDS_CREATE: 'product_feeds/create',
  PRODUCT_FEEDS_UPDATE: 'product_feeds/update',

  // Refunds
  REFUNDS_CREATE: 'refunds/create',

  // Shop
  SHOP_UPDATE: 'shop/update',
  SHOP_REDACT: 'shop/redact',

  // Themes
  THEMES_CREATE: 'themes/create',
  THEMES_UPDATE: 'themes/update',
  THEMES_DELETE: 'themes/delete',
  THEMES_PUBLISH: 'themes/publish',

  // Other
  SUBSCRIPTION_BILLING_ATTEMPTS_SUCCESS:
    'subscription_billing_attempts/success',
  SUBSCRIPTION_BILLING_ATTEMPTS_FAILURE:
    'subscription_billing_attempts/failure',
  SUBSCRIPTION_BILLING_ATTEMPTS_CHALLENGED:
    'subscription_billing_attempts/challenged',
  SUBSCRIPTION_CONTRACTS_CREATE: 'subscription_contracts/create',
  SUBSCRIPTION_CONTRACTS_UPDATE: 'subscription_contracts/update',
  TENDER_TRANSACTIONS_CREATE: 'tender_transactions/create',
  COMPANY_LOCATIONS_CREATE: 'company_locations/create',
  COMPANY_LOCATIONS_UPDATE: 'company_locations/update',
  COMPANY_LOCATIONS_DELETE: 'company_locations/delete',
} as const;

export type WebhookTopicKey = keyof typeof WebhookTopic;
export type WebhookTopicValue = (typeof WebhookTopic)[WebhookTopicKey];

/**
 * Reverse mapping from event name to GraphQL enum
 */
export const WebhookTopicReverse = Object.entries(WebhookTopic).reduce(
  (acc, [key, value]) => {
    acc[value] = key as WebhookTopicKey;
    return acc;
  },
  {} as Record<WebhookTopicValue, WebhookTopicKey>
);

/**
 * Helper function to convert GraphQL topic enum to event name
 * @example
 * getWebhookEventName('ORDERS_CREATE') // returns 'orders/create'
 */
export function getWebhookEventName(topic: WebhookTopicKey): WebhookTopicValue {
  return WebhookTopic[topic];
}

/**
 * Helper function to convert event name to GraphQL topic enum
 * @example
 * getWebhookTopicEnum('orders/create') // returns 'ORDERS_CREATE'
 */
export function getWebhookTopicEnum(
  eventName: WebhookTopicValue
): WebhookTopicKey {
  return WebhookTopicReverse[eventName];
}
